import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CardActionArea, 
  Box, 
  Chip, 
  Rating, 
  Avatar,
  Button,
  Divider,
  Paper,
  Container,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Pagination,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import { getAllAgents, getAgentCategories } from '../../api';

const AgentList = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAgents, setTotalAgents] = useState(0);
  const limit = 12;

  // Fetch agents data
  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit,
        search: searchTerm || undefined,
        category: selectedCategory || undefined,
        sortBy,
        sortOrder
      };

      const response = await getAllAgents(params);
      setAgents(response.data.data);
      setTotalPages(response.data.totalPages);
      setTotalAgents(response.data.total);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to load agents. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await getAgentCategories();
      setCategories(response.data.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch agents when filters change
  useEffect(() => {
    fetchAgents();
  }, [page, searchTerm, selectedCategory, sortBy, sortOrder]);

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  // Handle category filter
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1); // Reset to first page when filtering
  };

  // Handle sort change
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1); // Reset to first page when sorting
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSortBy('rating');
    setSortOrder('desc');
    setPage(1);
  };

  if (loading && agents.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading AI specialists...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 6,
          mb: 4,
          borderRadius: 4,
          textAlign: 'center'
        }}
      >
        <Typography variant="h3" gutterBottom fontWeight="bold">
          Hire AI Employees
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
          Discover talented AI specialists ready to transform your city operations
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            icon={<WorkIcon />} 
            label="24/7 Availability" 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
          />
          <Chip 
            icon={<StarIcon />} 
            label="Expert Specialists" 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
          />
          <Chip 
            icon={<BusinessIcon />} 
            label="Smart City Focus" 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
          />
        </Box>
      </Paper>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search AI specialists..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={handleCategoryChange}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={handleSortChange}
              >
                <MenuItem value="rating">Rating</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="price">Price</MenuItem>
                <MenuItem value="createdAt">Newest</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              sx={{ height: 56 }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Summary */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {totalAgents} AI specialists found
        </Typography>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Job Listings Grid */}
      <Grid container spacing={3}>
        {agents.map((agent) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={agent.agentId}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(0,0,0,0.05)',
                '&:hover': { 
                  transform: 'translateY(-8px)', 
                  boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                  border: '1px solid rgba(102, 126, 234, 0.3)'
                },
                position: 'relative',
                minHeight: 320,
                overflow: 'visible'
              }}
            >
              <CardActionArea 
                onClick={() => navigate(`/agent-marketplace/${agent.agentId}`)}
                sx={{ height: '100%', p: 0 }}
              >
                {/* Header with Avatar and Status */}
                <Box sx={{ 
                  p: 3, 
                  pb: 2, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                }}>
                  <Avatar
                    src={agent.icon}
                    alt={agent.name}
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      mx: 'auto', 
                      mb: 2,
                      border: '4px solid white',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Typography variant="h6" fontWeight="bold" color="text.primary">
                    {agent.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {agent.category} Specialist
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Rating value={agent.rating || 0} precision={0.1} readOnly size="small" />
                    <Typography variant="body2" color="text.secondary">
                      ({agent.rating})
                    </Typography>
                  </Box>
                </Box>

                <CardContent sx={{ p: 3, pt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    minHeight: 40, 
                    mb: 2,
                    lineHeight: 1.5
                  }}>
                    {agent.shortDescription}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: 16 }} />
                        Remote
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WorkIcon sx={{ fontSize: 16 }} />
                        Full-time
                      </Typography>
                    </Box>
                    <Chip
                      label={agent.price}
                      color="primary"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: 14,
                        bgcolor: 'primary.main',
                        color: 'white'
                      }}
                    />
                  </Box>
                </CardContent>

                {/* Apply Button */}
                <Box sx={{ p: 3, pt: 0 }}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: 'bold',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                      }
                    }}
                  >
                    View Profile
                  </Button>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Footer CTA */}
      <Paper 
        elevation={0}
        sx={{ 
          mt: 6,
          p: 4, 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          borderRadius: 4
        }}
      >
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Can't find the right AI specialist?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
          Contact us to discuss custom AI solutions tailored to your specific needs
        </Typography>
        <Button 
          variant="outlined" 
          size="large"
          sx={{ 
            color: 'white', 
            borderColor: 'white',
            '&:hover': {
              borderColor: 'white',
              bgcolor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          Request Custom Solution
        </Button>
      </Paper>
    </Container>
  );
};

export default AgentList; 